import { useCallback, useRef, useState } from "react";
import { startLogin, requestChallenge, verifyCode } from "../../../services/mfa/mfaApi";
import { describeMfaError } from "../../../services/mfa/mfaErrorMessages";
import { normaliseCode } from "./mfaCodeFormat";
import useLoginEnrollment from "./useLoginEnrollment";
import useLoginSetup, { isSetupAnswer } from "./useLoginSetup";
import { LOGIN_SETUP } from "../../../config/featureFlags";
import useCountdown from "./useCountdown";

const NEEDS_CHALLENGE = ["email", "sms"];
// METHOD_UNAVAILABLE on a resend means the server's send limit for this tx (tx_max_sends) is used up.
const SEND_LIMIT_MESSAGE = "No more codes can be sent for this sign-in. Use another method, or sign in again.";
const INVALID_FORMAT = {
  tin: "Enter the last 4 digits of your SSN or TIN.",
  default: "Enter the 6-digit code.",
};

// Login as a transaction: credentials -> mfa_tx -> code -> session. `onSession(session)` is
// called ONLY after /auth/mfa/verify (or a trusted-device start) returns one; nothing is
// stored here. The tx, password and codes live in memory for this component's lifetime.
const useMfaLogin = ({ getFingerprint, onSession }) => {
  const [stage, setStage] = useState("credentials"); // credentials | mfa | phone | enroll | mobile | expired
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [emailMasked, setEmailMasked] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  // LOGIN_SETUP (docs/specs/login-setup.md §1, §3): the channel whose code failed, and the steps still to come.
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [setupRequired, setSetupRequired] = useState([]);
  // Oracle's tin_offer_after_sec: when the SSN/TIN entry opens (docs/specs/authenticator-mfa-doc-gaps.md, part 1)
  const [tinReadyAt, setTinReadyAt] = useState(null);
  const tinWaitSeconds = useCountdown(tinReadyAt);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendBlocked, setResendBlocked] = useState(false);
  const txRef = useRef(null);
  const busyRef = useRef(false); // sync guard: state updates are too late to stop a double click

  const run = useCallback(async (work, context) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      const d = describeMfaError(e, context);
      setError(d.message);
      if (d.restart) setStage("expired");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const enroll = useLoginEnrollment({ run, txRef, setStage, onSession });
  const setup = useLoginSetup({ run, txRef, setStage, setError, beginEnrollment: enroll.beginEnrollment, onSession });

  const start = useCallback(
    (username, password) =>
      run(async () => {
        const fingerprint = await getFingerprint();
        const res = await startLogin({ username, password, fingerprint });
        if (res.session) return onSession(res.session);
        txRef.current = res.mfa_tx;
        setMethods(res.methods ?? []);
        setMethod(res.default_method ?? res.methods?.[0] ?? null);
        if (LOGIN_SETUP) setDefaultMethod(res.default_method ?? null);
        if (LOGIN_SETUP) setSetupRequired(res.setup_required ?? []);
        setEmailMasked(res.email_masked ?? "");
        setPhoneMasked(res.phone_masked ?? "");
        setTinReadyAt(res.tin_available_in > 0 ? Date.now() + res.tin_available_in * 1000 : null);
        setResendBlocked(false);
        setStage("mfa");
      }, LOGIN_SETUP ? { stage: "start" } : undefined),
    [run, getFingerprint, onSession],
  );

  const selectMethod = useCallback(
    (next) =>
      run(async () => {
        setMethod(next);
        if (NEEDS_CHALLENGE.includes(next)) await requestChallenge({ mfaTx: txRef.current, method: next });
      }),
    [run],
  );

  const resend = useCallback(
    () =>
      run(async () => {
        try {
          await requestChallenge({ mfaTx: txRef.current, method });
        } catch (e) {
          if (e?.response?.data?.error?.code !== "METHOD_UNAVAILABLE") throw e;
          setResendBlocked(true);
          setError(SEND_LIMIT_MESSAGE);
        }
      }),
    [run, method],
  );

  const verify = useCallback(
    (raw) =>
      run(async () => {
        const code = normaliseCode(method, raw);
        if (!code) return setError(INVALID_FORMAT[method] ?? INVALID_FORMAT.default);
        const res = await verifyCode({ mfaTx: txRef.current, method, code }).catch((e) => {
          const refused = e?.response?.data?.error;
          if (method === "tin" && refused?.code === "METHOD_UNAVAILABLE" && refused.retry_after > 0) {
            setTinReadyAt(Date.now() + refused.retry_after * 1000);
          }
          throw e;
        });
        if (LOGIN_SETUP && isSetupAnswer(res)) return setup.handle(res);
        if (method === "tin" && res?.enroll_required) return enroll.beginEnrollment(res);
        if (!res?.session) throw new Error("verify returned no session");
        // busy stays on until the caller finishes signing in.
        await onSession(res.session);
      }, { method }),
    [run, method, onSession, enroll, setup],
  );

  const restart = useCallback(() => {
    txRef.current = null;
    setStage("credentials");
    setMethods([]);
    setMethod(null);
    setEmailMasked("");
    setPhoneMasked("");
    setTinReadyAt(null);
    setError(null);
    setResendBlocked(false);
    setDefaultMethod(null); setSetupRequired([]);
    enroll.resetEnrollment();
    setup.reset();
  }, [enroll, setup]);

  return {
    stage, methods, method, emailMasked, phoneMasked, tinWaitSeconds, error, busy, resendBlocked,
    start, selectMethod, verify, resend, restart,
    enrollment: enroll.enrollment, enrollError: enroll.enrollError,
    confirmEnrollment: enroll.confirmEnrollment, cancelEnrollment: enroll.cancelEnrollment,
    mobileToken: enroll.mobileToken, finishMobileStep: enroll.finishMobileStep,
    defaultMethod, setupRequired, enrollStepLabel: enroll.enrollStepLabel, inSetup: enroll.inSetup,
    setup: { ...setup, newQr: enroll.newQr },
  };
};

export default useMfaLogin;
